import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import { FS } from "src/configs/constance";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import { collectionSort } from "src/utils";
import { RarityMapping } from "src/utils/knight";
import multiCall from "src/utils/multicall";
import CollectionQuery from "src/utils/query/onchain/multi-call-query/collection-query";
import EquipmentQuery from "src/utils/query/onchain/multi-call-query/equipment-query";

const initialState = {
  nftInventory: { v1: {}, v2: {} },
  collections: { v1: [], v2: [], specials: [] },
  forgingInventory: { v1: {}, v2: {} },
  selectedInventory: undefined,
  fetching: {
    nft: FS.INITIAL,
    collection: FS.INITIAL,
    forging: FS.INITIAL,
  },
};

export const fetchNFTInventory = createAsyncThunk("inventory2/fetchNFTInventory", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  thunkAPI.dispatch(inventory2Slice.actions.setFetching({ status: FS.FETCHING, type: "nft" }));
  return await EquipmentQuery.fetchEquipmentByAddress(accountAddress);
});

export const fetchForgingInventory = createAsyncThunk("inventory2/fetchForgingInventory", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  thunkAPI.dispatch(inventory2Slice.actions.setFetching({ status: FS.FETCHING, type: "forging" }));
  return await EquipmentQuery.fetchForgingEquipmentByAddress(accountAddress);
});

export const fetchCollectionsV2 = createAsyncThunk("inventory2/fetchCollectionsV2", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  thunkAPI.dispatch(inventory2Slice.actions.setFetching({ status: FS.FETCHING, type: "collection" }));
  const collectionLength = await NFTCollectionContract.getBalance(accountAddress);

  const [collectionIds] = await Promise.all([
    multiCall(
      NFTCollectionContract.ABI,
      new Array(collectionLength).fill(1).map((_, index) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "tokenOfOwnerByIndex",
        params: [accountAddress, index],
      }))
    ),
  ]);
  const collectionIdsFlattened = collectionIds.flat();

  const { normalCollections, specialCollections } = await CollectionQuery.fetchBasicCollections(collectionIdsFlattened);

  const armorTokenIdArray = [];
  const helmetTokenIdArray = [];
  const shieldTokenIdArray = [];
  const weaponTokenIdArray = [];
  normalCollections.forEach((item, _) => {
    armorTokenIdArray.push(item.armorTokenId);
    helmetTokenIdArray.push(item.helmetTokenId);
    shieldTokenIdArray.push(item.shieldTokenId);
    weaponTokenIdArray.push(item.weaponTokenId);
  });

  const normalItemsCollections = await CollectionQuery.fetchNormalItems(
    armorTokenIdArray,
    helmetTokenIdArray,
    shieldTokenIdArray,
    weaponTokenIdArray
  );

  const v1 = [];
  const v2 = [];
  let counter = 0;
  for (const rawCollection of normalCollections) {
    if (rawCollection.setId == 1) v1.push({ ...rawCollection, ...normalItemsCollections[counter] });
    else if (rawCollection.setId == 2) v2.push({ ...rawCollection, ...normalItemsCollections[counter] });
    counter++;
  }
  return { v1: v1.sort(collectionSort), v2: v2.sort(collectionSort), specials: specialCollections };
});

const inventory2Slice = createSlice({
  name: "inventory2Slice",
  initialState: initialState,
  reducers: {
    setFetching(state, action) {
      const type = action.payload.type;
      if (type == "nft") state.fetching.nft = action.payload.status;
      else if (type == "collection") state.fetching.collection = action.payload.status;
      else if (type == "forging") state.fetching.forging = action.payload.status;
    },
    setSelectedInventory(state, action) {
      state.selectedInventory = action.payload;
    },
    addInventoryItem(state, action) {
      const { tokenData, version } = action.payload;
      if (version == "1") state.nftInventory.v1[tokenData.tokenId] = tokenData;
      else if (version == "2") state.nftInventory.v2[tokenData.tokenId] = tokenData;
    },
    removeInventoryItems(state, action) {
      const { tokenIdList, version } = action.payload;
      tokenIdList.forEach((id) => {
        if (version == "1") {
          if (state.nftInventory.v1[id]) delete state.nftInventory.v1[id];
        } else if (state.nftInventory.v2[id]) delete state.nftInventory.v2[id];
      });
    },
    removeForgingItem(state, action) {
      const { tokenId, version } = action.payload;
      if (version == "1") {
        if (state.forgingInventory.v1[tokenId]) delete state.forgingInventory.v1[tokenId];
      } else if (state.forgingInventory.v2[tokenId]) delete state.forgingInventory.v2[tokenId];
    },
    addCollectionItems(state, action) {
      const { collectionDataList, version } = action.payload;
      if (version == "1") state.collections.v1 = state.collections.v1.concat(collectionDataList);
      else if (version == "2") state.collections.v2 = state.collections.v2.concat(collectionDataList);
      else if (version == "limited") state.collections.specials.concat(collectionDataList);
    },
    removeCollectionItems(state, action) {
      const { collectionIds, version } = action.payload;
      if (version == "1") {
        collectionIds.forEach((id) => {
          const index = state.collections.v1.findIndex((item) => item.id == id);
          if (index > -1) state.collections.v1.splice(index, 1);
        });
      } else if (version == "2") {
        collectionIds.forEach((id) => {
          const index = state.collections.v2.findIndex((item) => item.id == id);
          if (index > -1) state.collections.v2.splice(index, 1);
        });
      } else if (version == "limited") {
        collectionIds.forEach((id) => {
          const index = state.collections.specials.findIndex((item) => item.id == id);
          if (index > -1) state.collections.specials.splice(index, 1);
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNFTInventory.fulfilled, (state, action) => {
        state.nftInventory = action.payload;
        state.fetching.nft = FS.SUCCESS;
      })
      .addCase(fetchNFTInventory.rejected, (state, action) => {
        console.error(action);
        state.nftInventory = { v1: {}, v2: {} };
        state.fetching.nft = FS.FAIL;
      })
      .addCase(fetchForgingInventory.fulfilled, (state, action) => {
        state.forgingInventory = action.payload;
        state.fetching.forging = FS.SUCCESS;
      })
      .addCase(fetchForgingInventory.rejected, (state) => {
        state.forgingInventory = { v1: {}, v2: {} };
        state.fetching.forging = FS.FAIL;
      })
      .addCase(fetchCollectionsV2.fulfilled, (state, action) => {
        state.collections = action.payload;
        state.fetching.collection = FS.SUCCESS;
      })
      .addCase(fetchCollectionsV2.rejected, (state, action) => {
        console.error(action);
        state.collections = { v1: [], v2: [], specials: [] };
        state.fetching.collection = FS.FAIL;
      });
  },
});

export default inventory2Slice.reducer;
export const {
  setSelectedInventory,
  addInventoryItem,
  removeInventoryItems,
  removeForgingItem,
  addCollectionItems,
  removeCollectionItems,
} = inventory2Slice.actions;

export const selectCollectionsByRarity = createDraftSafeSelector(
  (state) => state.inventory2Slice.collections,
  (_, props) => props.rarity,
  (collections, rarity) => {
    if (!collections || !rarity) return [];
    const rarityNumber = RarityMapping.findIndex((item) => item === rarity);
    return collections.v1
      .filter((item) => item.rarity >= rarityNumber + 1 && item.rarity <= 5)
      .map((item) => ({ ...item, rarity: RarityMapping[item.rarity - 1] }));
  }
);

export const selectCollectionsByLevel = createDraftSafeSelector(
  (state) => state.inventory2Slice.collections,
  (_, props) => props.rarities,
  (collections, acceptableRarities) => {
    if (!collections || !acceptableRarities) return [];
    return collections.v1
      .filter((item) => acceptableRarities.includes(item.rarity))
      .map((item) => ({ ...item, rarity: RarityMapping[item.rarity - 1] }));
  }
);
